import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteIcon from '@mui/icons-material/Delete'
import EditRoadIcon from '@mui/icons-material/EditRoad'
import { Button, Divider, Grid, MenuItem, Select, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { usePromiseTracker } from 'react-promise-tracker'
import { useParams } from 'react-router'
import ConfirmBox from 'src/components/ConfirmBox'
import SuspenseLoader from 'src/components/SuspenseLoader'
import { AdminPermissionsService } from 'src/services/adminPermissionsService'
import MasterWorkoutExerciseListViewModel from 'src/viewmodels/MasterWorkoutExerciseListViewModel'
import MasterWorkoutExerciseViewModel from 'src/viewmodels/MasterWorkoutExerciseViewModel'
import CreateOrEditMasterWorkoutExercise from '../CreateOrEdit'
import MasterWorkoutExerciseBatchEdit from './MasterWorkoutExerciseBatchEdit'
import MasterWorkoutExerciseDaysList from './MasterWorkoutExerciseDaysList'
interface MasterWorkoutExercisesProps {
	viewModel: MasterWorkoutExerciseListViewModel
}

const MasterWorkoutExercises: React.FC<MasterWorkoutExercisesProps> = observer(({ viewModel }) => {
	const params = useParams<{ id: string }>()
	const {
		fetch,
		getMasterWorkoutExerciseLookups,
		deleteMany,
		saveChanges,
		groupByDayMasterWorkoutExercises,
		activeWeek,
		setActiveWeek,
		weeks,
		days,
		masterWorkout,
		clipboardWeek,
		copyWeek,
		pasteWeek,
		clearWeek,
		updateMasterWorkoutExerciseOrder,
		batchEditMasterWorkoutExercises,
		setActiveMasterWorkoutExercise,
		createMasterWorkoutExercise,
		activeMasterWorkoutExercise,
		lookups,
	} = viewModel

	useEffect(() => {
		fetch(params?.id)
		getMasterWorkoutExerciseLookups()
	}, [params?.id])

	useEffect(() => {
		if (masterWorkout) {
			const lastActiveWeek = localStorage.getItem(`${masterWorkout.id}`)
			if (lastActiveWeek) setActiveWeek(+lastActiveWeek)
		}
	}, [masterWorkout])

	const handleChangeActiveWeek = useCallback(
		(week: number) => {
			localStorage.setItem(`${masterWorkout.id}`, `${week}`)
			setActiveWeek(week)
		},
		[masterWorkout?.id],
	)

	const [deletedVms, setDeletedVms] = useState<MasterWorkoutExerciseViewModel[]>([])
	const [confirmBoxTitle, setConfirmBoxTitle] = useState<string>('')

	const handleDelete = useCallback((masterWorkoutExerciseViewModels: MasterWorkoutExerciseViewModel[]) => {
		setDeletedVms(masterWorkoutExerciseViewModels)
		setConfirmBoxTitle('Delete Master Workout Exercise')
	}, [])

	const handleClearWeek = useCallback(() => {
		setConfirmBoxTitle('Clear current week')
	}, [])

	const onConfirmDelete = useCallback(
		(title: string) => {
			switch (title) {
				case 'Delete Master Workout Exercise':
					deleteMany(deletedVms)
					setDeletedVms([])
					setConfirmBoxTitle('')
					break

				case 'Clear current week':
					clearWeek()
					setConfirmBoxTitle('')
					break

				default:
					break
			}
		},
		[deletedVms],
	)

	const [batchEditWeek, setBatchEditWeek] = useState(0)
	const handleConfirmBatchEdit = useCallback((values: any) => {
		batchEditMasterWorkoutExercises(values)
		setBatchEditWeek(0)
	}, [])

	const { canDelete, verifyEdit } = AdminPermissionsService.instance?.masterWorkoutPage
	const canEdit = verifyEdit(masterWorkout.isPublic)

	const { promiseInProgress } = usePromiseTracker()

	return promiseInProgress ? (
		<SuspenseLoader />
	) : (
		<>
			<Grid container direction="row" alignItems="stretch" spacing={3} mt={1}>
				{/* HEADER */}
				<Grid item xs={12}>
					<Grid container>
						<Grid item xs={2} display="flex" justifyContent="start">
							<Select label="Week" value={activeWeek} onChange={(e) => handleChangeActiveWeek(e.target.value as number)}>
								{weeks.map((weekNum: number) => (
									<MenuItem key={weekNum} value={weekNum}>
										Week #{weekNum}
									</MenuItem>
								))}
							</Select>
						</Grid>

						<Grid item xs={10} display="flex" justifyContent="end" alignItems="center">
							{canDelete && (
								<Button sx={{ mt: { xs: 2, md: 0 }, mx: 1 }} variant="outlined" startIcon={<DeleteIcon fontSize="small" />} onClick={handleClearWeek}>
									Clear Week
								</Button>
							)}
							{canEdit && (
								<>
									{clipboardWeek.copiedWeek != activeWeek && (
										<Button sx={{ mt: { xs: 2, md: 0 }, mx: 1 }} variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyWeek}>
											Copy
										</Button>
									)}
									{clipboardWeek.copiedWeek && clipboardWeek.copiedWeek != activeWeek && (
										<Button sx={{ mt: { xs: 2, md: 0 }, mx: 1 }} variant="outlined" startIcon={<ContentPasteIcon fontSize="small" />} onClick={pasteWeek}>
											Paste
										</Button>
									)}
									<Button
										sx={{ mt: { xs: 2, md: 0 }, mx: 1 }}
										variant="outlined"
										startIcon={<EditRoadIcon fontSize="small" />}
										onClick={() => setBatchEditWeek(activeWeek)}>
										Batch Edit
									</Button>
									{/* <Button sx={{ mt: { xs: 2, md: 0 }, mx: 1 }} variant="contained" onClick={saveChanges}>
										Save Changes
									</Button> */}
								</>
							)}
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Grid container spacing={4}>
						<Grid item xs={7} justifyContent="center" sx={{ height: '825px', overflowY: 'auto' }}>
							<Grid container>
								<Grid item xs={12}>
									<Typography variant="h4">Week {activeWeek} Exercises</Typography>
								</Grid>
								<Grid item xs={12} mt={1}>
									<Divider variant="fullWidth" />
								</Grid>
								<Grid item xs={12}>
									<MasterWorkoutExerciseDaysList
										items={groupByDayMasterWorkoutExercises}
										days={days}
										onCreateMWE={createMasterWorkoutExercise}
										onEditMWE={setActiveMasterWorkoutExercise}
										onOrderChange={updateMasterWorkoutExerciseOrder}
										canEdit={canEdit}
										activeMasterWorkoutExercise={activeMasterWorkoutExercise}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={5} justifyContent="center">
							{activeMasterWorkoutExercise ? (
								<Grid container>
									<Grid item display="flex" justifyContent="space-between" xs={12}>
										<Typography variant="h4">Exercise Settings</Typography>
										{canEdit && (
											<Button size="small" variant="contained" color="error" onClick={() => handleDelete([activeMasterWorkoutExercise])}>
												DELETE EXERCISE
											</Button>
										)}
									</Grid>
									<Grid item xs={12} my={1}>
										<Divider variant="fullWidth" />
									</Grid>
									<Grid item>
										<CreateOrEditMasterWorkoutExercise
											masterWorkout={masterWorkout}
											masterWorkoutExerciseViewModel={activeMasterWorkoutExercise}
											lookups={lookups}
											masterWorkoutWeightSelection={masterWorkout?.weightSelection}
											canEdit={canEdit}
										/>
									</Grid>
								</Grid>
							) : (
								<h1> </h1>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<MasterWorkoutExerciseBatchEdit onCancel={() => setBatchEditWeek(0)} onConfirm={handleConfirmBatchEdit} week={batchEditWeek} />
			<ConfirmBox onCancel={() => setConfirmBoxTitle('')} onConfirm={onConfirmDelete} title={confirmBoxTitle} />
		</>
	)
})

export default MasterWorkoutExercises
